import { useState } from 'react';

interface SortConfig {
    key: string;
    direction: 'ascending' | 'descending' | null;
}

export default function useTableSort({
    defaultSortConfig = {
        key: '',
        direction: null
    }
}: {
    defaultSortConfig?: SortConfig
}) {
    const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSortConfig || { key: '', direction: null });

    const getLengthOfCommaSeparatedValues = (value: string) => {
        try {
            if (typeof value === 'string' && value?.length > 0) {
                return value.split(',').length;
            }
            return 0;
        } catch (error) {
            console.error('Error in getLengthOfCommaSeparatedValues:', error);
            return 0;
        }
    }

    function sortData(data: any[]) {
        try {
            if (!sortConfig.key || sortConfig.direction === null) {
                return data;
            }

            const sortableData = [...data];

            return sortableData.sort((a, b) => {
                // Skip sorting for group header rows
                if (a.hasOwnProperty('groupName') || b.hasOwnProperty('groupName')) {
                    return 0;
                }

                // Get values to compare
                let aValue = a[sortConfig.key] || '';
                let bValue = b[sortConfig.key] || '';

                if (sortConfig.key === 'SkillsFormatted') {
                    try {
                        let tempA = a.AgentSkills || ""
                        let tempB = b.AgentSkills || ""
                        if (tempA.length > 0) {
                            aValue = getLengthOfCommaSeparatedValues(tempA)
                        }
                        if (tempB.length > 0) {
                            bValue = getLengthOfCommaSeparatedValues(tempB)
                        }
                    } catch (error) {
                        console.error('Error in sortData skills:', error);
                    }
                }
                if (sortConfig.key === 'GroupsFormatted') {
                    try {
                        let tempA = a.AgentGroups || ""
                        let tempB = b.AgentGroups || ""
                        if (tempA.length > 0) {
                            aValue = getLengthOfCommaSeparatedValues(tempA)
                        }
                        if (tempB.length > 0) {
                            bValue = getLengthOfCommaSeparatedValues(tempB)
                        }
                    } catch (error) {
                        console.error('Error in sortData skills:', error);
                    }
                }

                // Handle complex objects (for fields with warning)
                if (typeof aValue === 'object' && aValue !== null) {
                    aValue = aValue.value || '';
                }
                if (typeof bValue === 'object' && bValue !== null) {
                    bValue = bValue.value || '';
                }

                // Convert to appropriate types for comparison
                if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
                    // Handle numbers
                    aValue = Number(aValue);
                    bValue = Number(bValue);
                } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                    // Handle strings with case-insensitive comparison
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                // Sort based on direction
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }

                return 0;
            });
        } catch (error) {
            console.error('Error sorting data:', error);
            return data;
        }
    }

    function getSortedGroupData(data: any[], finalTableData: any[]) {
        try {
            const sortedData = sortData(data);
            return finalTableData.concat(sortedData);
        } catch (error) {
            console.error('Error sorting group data:', error);
            return finalTableData.concat(data);
        }
    }

    function sortGroupedData(groupedData: any, groupingColumns: any[] = []) {
        try {
            // Find which level in the hierarchy the clicked column belongs to
            const clickedColumnLevel = groupingColumns.findIndex(col =>
                col.id === sortConfig.key ||
                (col.id === 'AgentSkills' && sortConfig.key === 'SkillsFormatted') || (col.id === 'AgentGroups' && sortConfig.key === 'GroupsFormatted')
            );

            // If not a grouped column, keep default alphabetical order at all levels
            if (clickedColumnLevel === -1) {
                return sortGroupedDataRecursively(groupedData, 0, groupingColumns, false);
            }

            // Sort only at the specific level where the clicked column exists
            return sortGroupedDataRecursively(groupedData, 0, groupingColumns, true, clickedColumnLevel);

        } catch (error) {
            console.error('Error sorting grouped data:', error);
            return groupedData;
        }
    }

    function sortGroupedDataRecursively(data: any, currentLevel: number, groupingColumns: any[], shouldSort: boolean, targetLevel?: number): any {
        try {
            if (!data || typeof data !== 'object') {
                return data;
            }

            // If this is an array (leaf level), return as is
            if (Array.isArray(data)) {
                return data;
            }

            const entries = Object.entries(data);

            // Determine if we should sort at this level
            const sortAtThisLevel = shouldSort &&
                sortConfig.key &&
                sortConfig.direction &&
                (targetLevel === undefined || currentLevel === targetLevel);

            // Sort entries if needed
            if (sortAtThisLevel) {
                entries.sort(([groupNameA], [groupNameB]) => {
                    let aValue: any = String(groupNameA).toLowerCase();
                    let bValue: any = String(groupNameB).toLowerCase();
                    if (sortConfig.key === 'SkillsFormatted') {

                        if (groupNameA.length > 0) {
                            aValue = getLengthOfCommaSeparatedValues(groupNameA)
                        }
                        if (groupNameB.length > 0) {
                            bValue = getLengthOfCommaSeparatedValues(groupNameB)
                        }
                    }
                    if (sortConfig.key === 'GroupsFormatted') {
                        try {
                            if (groupNameA.length > 0) {
                                aValue = getLengthOfCommaSeparatedValues(groupNameA)
                            }
                            if (groupNameB.length > 0) {
                                bValue = getLengthOfCommaSeparatedValues(groupNameB)
                            }
                        } catch (error) {
                            console.error('Error in sortData skills:', error);
                        }
                    }

                    if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
                        // Handle numbers
                        aValue = Number(aValue);
                        bValue = Number(bValue);
                    } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                        // Handle strings with case-insensitive comparison
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                    }
                    if (aValue < bValue) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                });
            } else {
                // Keep alphabetical order at levels we're not sorting
                entries.sort(([groupNameA], [groupNameB]) => {
                    let aValue = String(groupNameA).toLowerCase();
                    let bValue = String(groupNameB).toLowerCase();
                    return aValue.localeCompare(bValue);
                });
            }

            // Recursively process child levels
            const result: any = {};
            entries.forEach(([groupName, groupData]) => {
                result[groupName] = sortGroupedDataRecursively(
                    groupData,
                    currentLevel + 1,
                    groupingColumns,
                    shouldSort,
                    targetLevel
                );
            });

            return result;
        } catch (error) {
            console.error('Error in recursive group sorting:', error);
            return data;
        }
    }

    const handleSort = (columnId: string) => {
        try {
            let direction: 'ascending' | 'descending' | null = 'ascending';

            if (sortConfig.key === columnId) {
                if (sortConfig.direction === 'ascending') {
                    direction = 'descending';
                } else if (sortConfig.direction === 'descending') {
                    direction = null;
                }
            }

            setSortConfig({ key: columnId, direction });
        } catch (error) {
            console.error('Error in handleSort:', error);
        }
    };

    const getSortIcon = (columnId: string) => {
        try {
            if (sortConfig.key !== columnId) return null;

            if (sortConfig.direction === 'ascending') {
                return <span style={{ marginLeft: '5px' }}>↑</span>;
            } else if (sortConfig.direction === 'descending') {
                return <span style={{ marginLeft: '5px' }}>↓</span>;
            }
            return null;
        } catch (error) {
            console.error("Error in getSortIcon", error);
            return null;
        }
    };

    return { sortData, handleSort, getSortIcon, getSortedGroupData, sortGroupedData };
}
